/* URBRAND Street Commerce 5.9.7 — released under the Unlicense. */
(() => {
  'use strict';

  const cfg = window.URB || {};
  const S = cfg.strings || {};
  const $ = (selector, root = document) => root?.querySelector(selector) || null;
  const $$ = (selector, root = document) => [...(root?.querySelectorAll(selector) || [])];
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const selectorEscape = (value) => window.CSS?.escape ? window.CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[char]);
  const safeUrl = (value = '') => {
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch {
      return '#';
    }
  };
  /* Shopify image URLs already carry ?v=; only then can width be appended with &. */
  const sizedImage = (value = '', width = 400) => {
    const url = safeUrl(value);
    if (url === '#') return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };
  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(cfg.locale || document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: cfg.currency || 'BRL'
      }).format(value);
    } catch {
      const fixed = value.toFixed(2).replace('.', ',');
      return (cfg.money || 'R$ {{amount_with_comma_separator}}')
        .replace('{{amount_with_comma_separator}}', fixed)
        .replace('{{amount}}', value.toFixed(2));
    }
  };
  const t = (key, fallback = '', vars = {}) => {
    let text = S[key] || fallback;
    Object.entries(vars).forEach(([name, value]) => {
      text = text.split(`__${name.toUpperCase()}__`).join(value);
    });
    return text;
  };
  const debounce = (fn, delay = 250) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
  };
  const notify = (text) => {
    const toast = $('#Toast');
    if (!toast || !text) return;
    toast.textContent = text;
    toast.classList.add('show');
    window.clearTimeout(window.urbToast);
    window.urbToast = window.setTimeout(() => toast.classList.remove('show'), 2800);
  };

  /* ------------------------------------------------------------------ *
   * Overlay manager — keeps body scroll lock in sync when several
   * dialogs/drawers can be open, and restores focus on close.
   * ------------------------------------------------------------------ */
  const openOverlays = new Set();
  const lastFocused = new WeakMap();
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const syncLock = () => document.body.classList.toggle('locked', openOverlays.size > 0 || Boolean(document.querySelector('dialog[open]')));
  const registerOpen = (node) => {
    if (!node) return;
    lastFocused.set(node, document.activeElement);
    openOverlays.add(node);
    syncLock();
  };
  const registerClose = (node) => {
    if (!node) return;
    openOverlays.delete(node);
    syncLock();
    const previous = lastFocused.get(node);
    if (previous && document.contains(previous) && typeof previous.focus === 'function') {
      previous.focus({ preventScroll: true });
    }
    lastFocused.delete(node);
  };
  const focusFirst = (node) => {
    if (!node) return;
    const target = $('[data-autofocus]', node) || $$(FOCUSABLE, node).find((el) => el.offsetParent !== null);
    window.setTimeout(() => target?.focus({ preventScroll: true }), 60);
  };
  const trapFocus = (event, node) => {
    if (event.key !== 'Tab') return;
    const items = $$(FOCUSABLE, node).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const openDialog = (dialog) => {
    if (!dialog) return;
    if (window.URBDialog?.open) {
      window.URBDialog.setup?.(dialog);
      openOverlays.add(dialog);
      syncLock();
      window.URBDialog.open(dialog);
      return;
    }
    registerOpen(dialog);
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else dialog.setAttribute('open', '');
    focusFirst(dialog);
  };
  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (window.URBDialog?.close) {
      openOverlays.delete(dialog);
      syncLock();
      lastFocused.delete(dialog);
      window.URBDialog.close(dialog);
      return;
    }
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else {
      dialog.removeAttribute('open');
      registerClose(dialog);
    }
  };
  /* Non-dialog drawers (mobile menu, facets) */
  const openPanel = (panel, className = 'open') => {
    if (!panel) return;
    panel.classList.add(className);
    panel.setAttribute('aria-hidden', 'false');
    registerOpen(panel);
    focusFirst(panel);
    if (panel.dataset.urbFocusTrap !== 'true') {
      panel.dataset.urbFocusTrap = 'true';
      panel.addEventListener('keydown', (event) => trapFocus(event, panel));
    }
  };
  const closePanel = (panel, className = 'open') => {
    if (!panel) return;
    panel.classList.remove(className);
    panel.setAttribute('aria-hidden', 'true');
    registerClose(panel);
  };

  /* ------------------------------------------------------------------ *
   * Wishlist
   * ------------------------------------------------------------------ */
  const wishlistKey = 'urbrand:wishlist:v2';
  const wishlistLegacyKey = 'urbrand:wishlist:v1';
  const wishlistDrawer = $('#WishlistDrawer');
  const wishlistBody = wishlistDrawer ? $('[data-wishlist-body]', wishlistDrawer) : null;
  const wishlistPixDiscount = Math.max(0, Math.min(100, Number(wishlistDrawer?.dataset.pixDiscount || 0)));
  const wishlistInstallments = Math.max(1, Math.min(3, Number(wishlistDrawer?.dataset.installments || 3)));
  const getWishlist = () => {
    try {
      let raw = localStorage.getItem(wishlistKey);
      if (!raw) {
        raw = localStorage.getItem(wishlistLegacyKey);
        if (raw) localStorage.setItem(wishlistKey, raw);
      }
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const setWishlist = (items) => {
    const limit = Math.max(10, Number(cfg.retention?.wishlistLimit || 40));
    const normalized = items.slice(0, limit);
    try {
      localStorage.setItem(wishlistKey, JSON.stringify(normalized));
    } catch { /* private mode */ }
    syncWishlistUI();
    document.dispatchEvent(new CustomEvent('urb:wishlist:updated', { detail: { items: normalized } }));
  };
  const syncWishlistUI = () => {
    const items = getWishlist();
    const ids = new Set(items.map((item) => String(item.id)));
    $$('[data-wishlist-count]').forEach((node) => {
      node.textContent = items.length;
      node.hidden = items.length === 0;
    });
    const drawerCount = $('[data-wishlist-drawer-count]', wishlistDrawer);
    if (drawerCount) drawerCount.textContent = `${items.length} ${items.length === 1 ? 'item salvo' : 'itens salvos'}`;
    $$('[data-wishlist-toggle]').forEach((button) => {
      const active = ids.has(String(button.dataset.productId));
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? t('wishlistRemove', 'Remover dos favoritos') : t('wishlistAdd', 'Adicionar aos favoritos'));
    });
    $('[data-wishlist-footer]', wishlistDrawer)?.toggleAttribute('hidden', items.length === 0);
    if (!wishlistBody) return;
    wishlistBody.innerHTML = items.length
      ? items.map((item) => {
        const price = Number(item.price || 0);
        const comparePrice = Number(item.compareAtPrice || 0);
        const pixPrice = wishlistPixDiscount > 0 ? Math.floor((price * (100 - wishlistPixDiscount)) / 100) : 0;
        const installmentPrice = wishlistInstallments > 1 ? Math.floor(price / wishlistInstallments) : 0;
        const originalPrice = comparePrice > price ? comparePrice : (pixPrice ? price : 0);
        return `<article class="wishlist-item">
          <a class="wishlist-item__media" href="${safeUrl(item.url)}" tabindex="-1" aria-hidden="true">${item.image ? `<img src="${sizedImage(item.image, 360)}" width="180" height="180" loading="lazy" alt="${escapeHtml(item.title)}">` : ''}</a>
          <div class="wishlist-item-content">
            ${item.vendor ? `<p class="wishlist-item__vendor">${escapeHtml(item.vendor)}</p>` : ''}
            <a class="wishlist-item__title" href="${safeUrl(item.url)}">${escapeHtml(item.title)}</a>
            ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<p class="wishlist-item__variant">${escapeHtml(item.variantTitle)}</p>` : ''}
            <div class="wishlist-item__pricing">
              ${originalPrice ? `<s>${formatMoney(originalPrice)}</s>` : ''}
              ${pixPrice ? `<p class="wishlist-item__pix"><span>PIX</span><strong>${formatMoney(pixPrice)}</strong><small>${wishlistPixDiscount}% OFF</small></p>` : `<strong>${formatMoney(price)}</strong>`}
              ${installmentPrice ? `<small class="wishlist-item__installments">ou ${wishlistInstallments}x de ${formatMoney(installmentPrice)} sem juros</small>` : ''}
            </div>
            <div class="wishlist-item-actions">
              <a class="button primary" href="${safeUrl(item.url)}">${escapeHtml(t('viewProduct', 'Ver produto'))}</a>
              <button class="wishlist-item__remove" type="button" data-wishlist-remove="${escapeHtml(item.id)}" aria-label="${escapeHtml(t('remove', 'Remover'))}: ${escapeHtml(item.title)}">${escapeHtml(t('remove', 'Remover'))}</button>
            </div>
          </div>
        </article>`;
      }).join('')
      : `<div class="wishlist-empty"><span aria-hidden="true">♡</span><h3>Seus favoritos</h3><p>${escapeHtml(t('emptyWishlist', 'Nenhum favorito salvo.'))}</p>
          <a class="button primary" href="${safeUrl(cfg.routes?.allProducts || '/collections/all')}">${escapeHtml(t('cartEmptyCta', 'Explorar produtos'))}</a></div>`;
  };

  /* ------------------------------------------------------------------ *
   * Product detail
   * ------------------------------------------------------------------ */
  const readJson = (node) => {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  };
  const selectedOptions = (root) => {
    const values = [];
    $$('[data-option]:checked', root).forEach((input) => {
      values[Number(input.dataset.optionPosition || 1) - 1] = input.value;
    });
    $$('[data-option-select]', root).forEach((select) => {
      values[Number(select.dataset.optionPosition || 1) - 1] = select.value;
    });
    return values.filter((value) => value !== undefined);
  };
  const selectedVariant = (root, variants, preferredId = '') => {
    if (!Array.isArray(variants)) return null;
    const byId = preferredId
      ? variants.find((variant) => String(variant.id) === String(preferredId))
      : null;
    if (byId) return byId;
    const selected = selectedOptions(root);
    return variants.find((variant) => variant.options.every((option, index) => option === selected[index])) || null;
  };
  const syncWhatsAppBuyLink = (root, variant) => {
    if (!root) return;
    $$('[data-whatsapp-buy]', root).forEach((link) => {
      const number = String(link.dataset.whatsappNumber || '').replace(/\D/g, '');
      const enabled = Boolean(variant?.available && number);
      link.classList.toggle('is-disabled', !enabled);
      link.setAttribute('aria-disabled', String(!enabled));
      link.tabIndex = enabled ? 0 : -1;
      if (!enabled) return;

      const form = link.closest('form');
      const quantityInput = $('input[name="quantity"]', form || root);
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value, 10) || 1);
      const baseUrl = new URL(link.dataset.whatsappProductUrl || window.location.href, window.location.origin);
      baseUrl.searchParams.set('variant', variant.id);
      const lines = [String(link.dataset.whatsappMessage || '').trim()];
      lines.push(`Produto: ${link.dataset.whatsappProductTitle || document.title}`);
      if (link.dataset.whatsappProductVendor) lines.push(`Marca: ${link.dataset.whatsappProductVendor}`);
      if (variant.title && variant.title !== 'Default Title') lines.push(`Variante: ${variant.title}`);
      if (variant.sku) lines.push(`SKU: ${variant.sku}`);
      lines.push(`Quantidade: ${quantity}`);
      lines.push(`Valor unitário: ${formatMoney(variant.price)}`);
      if (quantity > 1) lines.push(`Valor total: ${formatMoney(variant.price * quantity)}`);
      lines.push(`Link: ${baseUrl.href}`);
      link.href = `https://wa.me/${number}?text=${encodeURIComponent(lines.filter(Boolean).join('\n'))}`;
    });
  };
  const optionAvailability = (variants, selected, position, value) => {
    const candidates = variants.filter((variant) => (
      variant.options[position] === value
      && variant.options.every((option, index) => (
        index === position || !selected[index] || option === selected[index]
      ))
    ));
    return {
      exists: candidates.length > 0,
      available: candidates.some((variant) => variant.available)
    };
  };
  const selectNextAvailableColor = (root, variants) => {
    const colorGroup = $('.pdp__option--color', root);
    if (!colorGroup || !Array.isArray(variants)) return null;
    const position = Number(colorGroup.dataset.optionPosition || 1) - 1;
    const selected = selectedOptions(root);
    const colorInputs = $$('[data-option]', colorGroup).filter((input) => !input.dataset.productUrl);

    if (colorInputs.length) {
      const currentIndex = colorInputs.findIndex((input) => input.checked);
      const current = colorInputs[currentIndex];
      if (current && optionAvailability(variants, selected, position, current.value).available) return current;
      for (let offset = 1; offset <= colorInputs.length; offset += 1) {
        const candidate = colorInputs[(currentIndex + offset + colorInputs.length) % colorInputs.length];
        if (!optionAvailability(variants, selected, position, candidate.value).available) continue;
        candidate.checked = true;
        return candidate;
      }
      return null;
    }

    const colorSelect = $('[data-option-select]', colorGroup);
    if (!colorSelect) return null;
    const colorOptions = [...colorSelect.options].filter((option) => !option.dataset.productUrl);
    const currentIndex = colorOptions.findIndex((option) => option.selected);
    const current = colorOptions[currentIndex];
    if (current && optionAvailability(variants, selected, position, current.value).available) return current;
    for (let offset = 1; offset <= colorOptions.length; offset += 1) {
      const candidate = colorOptions[(currentIndex + offset + colorOptions.length) % colorOptions.length];
      if (!optionAvailability(variants, selected, position, candidate.value).available) continue;
      colorSelect.value = candidate.value;
      return candidate;
    }
    return null;
  };
  /* Sizes communicate global stock across every color. Other options remain
     contextual, so colors still reflect the currently selected size. */
  const markUnavailableOptions = (root, variants) => {
    const selected = selectedOptions(root);
    $$('[data-option]', root).forEach((input) => {
      const position = Number(input.dataset.optionPosition || 1) - 1;
      const linkedProduct = Boolean(input.dataset.productUrl);
      const isSize = Boolean(input.closest('.pdp__option--size'));
      const state = linkedProduct
        ? { exists: true, available: input.dataset.available !== 'false' }
        : optionAvailability(variants, isSize ? [] : selected, position, input.value);
      const available = state.exists && state.available;
      const label = root.querySelector(`label[for="${selectorEscape(input.id)}"]`);
      input.dataset.available = String(available);
      input.setAttribute('aria-disabled', String(!state.exists));
      label?.classList.toggle('is-unavailable', !available);
      label?.setAttribute('aria-disabled', String(!state.exists));
      label?.setAttribute('title', available ? '' : t('soldOut', 'Esgotado'));
      const unavailableText = label?.querySelector('[data-option-unavailable-text]');
      if (unavailableText) unavailableText.hidden = available;
    });
    $$('[data-option-select]', root).forEach((select) => {
      const position = Number(select.dataset.optionPosition || 1) - 1;
      const isSize = Boolean(select.closest('.pdp__option--size'));
      [...select.options].forEach((option) => {
        const linkedProduct = Boolean(option.dataset.productUrl);
        const state = linkedProduct
          ? { exists: true, available: option.dataset.available !== 'false' }
          : optionAvailability(variants, isSize ? [] : selected, position, option.value);
        const available = state.exists && state.available;
        option.dataset.available = String(available);
        option.disabled = !state.exists && !option.selected && !linkedProduct;
      });
    });
  };
  const updateProduct = (root, forcedVariantId = null) => {
    if (!root) return;
    const variants = readJson($('[data-variants]', root));
    if (!variants) return;
    const forcedVariant = forcedVariantId
      ? variants.find((item) => String(item.id) === String(forcedVariantId))
      : null;
    const selected = forcedVariant ? forcedVariant.options : selectedOptions(root);
    const variant = forcedVariant || selectedVariant(root, variants);
    if (!forcedVariant) markUnavailableOptions(root, variants);
    const sectionId = root.dataset.sectionId || '';
    const stickyBar = sectionId
      ? document.querySelector(`[data-sticky-buy][data-section-id="${selectorEscape(sectionId)}"]`)
      : $('[data-sticky-buy]');
    const setButtonState = (button, item) => {
      if (!button) return;
      const preorder = root.dataset.preorder === 'true';
      const addLabel = preorder
        ? (button.dataset.preorderLabel || t('preorder', 'Pré-venda'))
        : (button.dataset.addLabel || t('add', 'Adicionar à sacola'));
      const soldLabel = button.dataset.soldLabel || t('soldOut', 'Esgotado');
      const labelNode = $('[data-add-text]', button);
      const priceNode = $('[data-add-price]', button);
      const label = item?.available ? addLabel : soldLabel;
      button.disabled = !item?.available;
      if (labelNode) labelNode.textContent = label;
      else button.textContent = label;
      if (priceNode) {
        priceNode.textContent = item?.available ? formatMoney(item.price) : '';
        priceNode.hidden = !item?.available;
      }
      button.setAttribute('aria-disabled', String(!item?.available));
    };
    const variantStatus = $('[data-variant-status]', root);
    if (!variant) {
      if (variantStatus) variantStatus.textContent = t('variantUnavailable', 'Combinação de variante indisponível.');
      $$('[data-add]', root).forEach((button) => {
        const labelNode = $('[data-add-text]', button);
        const priceNode = $('[data-add-price]', button);
        button.disabled = true;
        if (labelNode) labelNode.textContent = t('unavailable', 'Indisponível');
        else button.textContent = t('unavailable', 'Indisponível');
        if (priceNode) {
          priceNode.textContent = '';
          priceNode.hidden = true;
        }
        button.setAttribute('aria-disabled', 'true');
      });
      setButtonState($('[data-sticky-add]', stickyBar), null);
      syncWhatsAppBuyLink(root, null);
      root.dispatchEvent(new CustomEvent('urb:variant-change', { bubbles: true, detail: { variant: null } }));
      return;
    }
    $$('[data-variant-id]', root).forEach((input) => {
      input.value = String(variant.id);
      input.setAttribute('value', String(variant.id));
    });
    if (variantStatus) {
      variantStatus.textContent = t('variantSelected', 'Variante selecionada: __VARIANT__.', { variant: variant.title || '' });
    }
    $$('[data-option-label]', root).forEach((label) => {
      const position = Number(label.dataset.optionLabel);
      label.textContent = selected[position - 1] || '';
    });
    $$('[data-product-price]:not([data-wishlist-toggle]):not([data-compare-toggle])', root).forEach((node) => { node.textContent = formatMoney(variant.price); });
    $$('[data-card-pix-price]', root).forEach((node) => {
      const discount = Math.max(0, Math.min(100, Number(node.dataset.pixDiscount) || 0));
      node.textContent = formatMoney(Math.floor(variant.price * (100 - discount) / 100));
    });
    const onSale = variant.compare_at_price > variant.price;
    $('[data-price-wrap]', root)?.classList.toggle('sale', onSale);
    $$('[data-product-price][data-card-compare-enabled]', root).forEach((node) => {
      const hasPixPrice = Boolean($('[data-card-pix-price]', root));
      node.classList.toggle('price-current--struck', node.dataset.cardCompareEnabled === 'true' && hasPixPrice && !onSale);
    });
    $$('[data-product-compare]', root).forEach((node) => {
      const enabled = node.dataset.enabled !== 'false';
      node.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      node.hidden = !(enabled && onSale);
    });
    $$('[data-product-discount]', root).forEach((node) => {
      const enabled = node.dataset.enabled !== 'false';
      const discount = onSale
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;
      node.textContent = discount ? `-${discount}%` : '';
      node.hidden = !(enabled && discount > 0);
    });
    $$('[data-add]', root).forEach((button) => setButtonState(button, variant));
    setButtonState($('[data-sticky-add]', stickyBar), variant);
    syncWhatsAppBuyLink(root, variant);
    $$('[data-sticky-price]', stickyBar || document).forEach((node) => { node.textContent = formatMoney(variant.price); });
    const stickyVariant = $('[data-sticky-variant]', stickyBar);
    if (stickyVariant) {
      stickyVariant.textContent = variant.title || '';
      stickyVariant.hidden = !variant.title || variant.title === 'Default Title';
    }
    const stickyImage = $('[data-sticky-image], .sticky-product img', stickyBar);
    if (stickyImage && variant.featured_image) {
      stickyImage.src = variant.featured_image;
      stickyImage.removeAttribute('srcset');
      stickyImage.alt = variant.featured_image_alt || '';
    }
    const sku = $('[data-product-sku]', root);
    if (sku) sku.textContent = variant.sku || '—';
    const barcode = $('[data-product-barcode]', root);
    if (barcode) barcode.textContent = variant.barcode || '—';
    const payments = $('[data-product-payments]', root);
    if (payments) {
      const installments = Math.max(1, Number(payments.dataset.installments || 1));
      const pixDiscount = Number(payments.dataset.pixDiscount || 0);
      const installmentText = $('[data-installment-text]', payments);
      const pixText = $('[data-pix-text]', payments);
      if (installmentText) {
        installmentText.textContent = t('installments', '__COUNT__x de __AMOUNT__ sem juros', {
          count: installments,
          amount: formatMoney(Math.floor(variant.price / installments))
        });
      }
      if (pixText) {
        const pixAmount = formatMoney(Math.floor((variant.price * (100 - pixDiscount)) / 100));
        const pixValue = $('[data-pix-value]', pixText);
        const pixDiscountText = $('[data-pix-discount]', pixText);
        if (pixValue) pixValue.textContent = pixAmount;
        if (pixDiscountText) pixDiscountText.textContent = `${pixDiscount}% OFF`;
        if (!pixValue) {
          pixText.textContent = t('pixPrice', '__AMOUNT__ no Pix (__PERCENT__% OFF)', {
            amount: pixAmount,
            percent: pixDiscount
          });
        }
      }
    }
    const inventory = $('[data-inventory-status]', root);
    if (inventory) {
      const threshold = Number(inventory.dataset.threshold || 8);
      const text = $('p', inventory);
      const bar = $('[data-inventory-bar]', inventory);
      inventory.classList.remove('low', 'sold');
      if (!variant.available) {
        inventory.classList.add('sold');
        if (text) text.textContent = inventory.dataset.soldOutText || t('soldOut', 'Esgotado');
        if (bar) bar.style.width = '0%';
      } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
        inventory.classList.add('low');
        const lowTemplate = inventory.dataset.lowStockText || t('lowStock', 'Restam __COUNT__ unidades', { count: '__COUNT__' });
        if (text) text.textContent = lowTemplate
          .replace('[quantidade]', String(variant.inventory_quantity))
          .replace('__COUNT__', String(variant.inventory_quantity));
        if (bar) bar.style.width = `${Math.max(6, Math.min(100, Math.round((variant.inventory_quantity / threshold) * 100)))}%`;
      } else {
        if (text) text.textContent = inventory.dataset.inStockText || t('inStock', 'Em estoque');
        if (bar) bar.style.width = '100%';
      }
    }
    const quantity = $('input[name="quantity"]', root);
    if (quantity && variant.quantity_rule) {
      quantity.min = variant.quantity_rule.min || 1;
      quantity.step = variant.quantity_rule.increment || 1;
      if (variant.quantity_rule.max) quantity.max = variant.quantity_rule.max;
      else quantity.removeAttribute('max');
      if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
      if (quantity.max && Number(quantity.value) > Number(quantity.max)) quantity.value = quantity.max;
    }
    const pickup = $('[data-pickup-availability]', root);
    if (pickup) {
      const heading = $('strong', pickup);
      const time = $('[data-pickup-time]', pickup);
      const showUnavailable = pickup.dataset.showUnavailable === 'true';
      if (variant.pickup_available) {
        pickup.hidden = false;
        if (heading) heading.textContent = pickup.dataset.availableHeading || '';
        if (time) {
          time.hidden = false;
          time.textContent = variant.pickup_time || '';
        }
      } else {
        pickup.hidden = !showUnavailable;
        if (heading) heading.textContent = pickup.dataset.unavailableHeading || '';
        if (time) {
          time.hidden = true;
          time.textContent = '';
        }
      }
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') !== String(variant.id)) {
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    const shareButton = $('[data-share]', root);
    if (shareButton) shareButton.dataset.shareUrl = url.href;
    root.dispatchEvent(new CustomEvent('urb:variant-change', { bubbles: true, detail: { variant } }));
  };
  const storeRecentlyViewed = () => {
    const current = readJson($('[data-current-product-json]'));
    if (!current) return;
    try {
      const stored = JSON.parse(localStorage.getItem('urbrand:recent:v1') || '[]');
      const items = (Array.isArray(stored) ? stored : [])
        .filter((item) => item && typeof item === 'object' && String(item.id) !== String(current.id));
      items.unshift(current);
      localStorage.setItem('urbrand:recent:v1', JSON.stringify(items.slice(0, 20)));
    } catch { /* Local storage is optional. */ }
  };
  const renderRecentlyViewed = () => {
    $$('[data-recently-viewed]').forEach(async (section) => {
      const current = section.dataset.currentProduct;
      const grid = $('[data-recent-grid]', section);
      if (!grid) return;
      let items = [];
      try {
        const stored = JSON.parse(localStorage.getItem('urbrand:recent:v1') || '[]');
        items = (Array.isArray(stored) ? stored : [])
          .filter((item) => item && typeof item === 'object' && item.handle && item.handle !== current);
      } catch {
        return;
      }
      if (!items.length) {
        section.hidden = true;
        return;
      }
      const limit = Number(section.dataset.limit || 4);
      const handles = items.slice(0, limit).map((item) => item.handle).join(',');
      if (section.dataset.renderedHandles === handles || section.dataset.recentLoading === 'true') return;
      section.dataset.recentLoading = 'true';
      try {
        const cards = await Promise.allSettled(items.slice(0, limit).map(async (item) => {
          const url = new URL(safeUrl(item.url), window.location.origin);
          url.searchParams.set('view', 'card');
          const response = await fetch(url.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) return null;
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          return documentFragment.querySelector('.product-card');
        }));
        const validCards = cards.filter((result) => result.status === 'fulfilled' && result.value).map((result) => result.value);
        if (!validCards.length) {
          section.hidden = true;
          return;
        }
        grid.replaceChildren(...validCards);
        section.dataset.renderedHandles = handles;
        section.hidden = false;
        initReveal(grid);
        syncWishlistUI();
        window.requestAnimationFrame(() => initRails(section));
      } catch {
        section.hidden = true;
      } finally {
        delete section.dataset.recentLoading;
      }
    });
  };

  const cardGalleryItems = (card) => readJson($('[data-card-gallery]', card)) || [];
  const setCardGalleryImage = (card, items, index) => {
    const image = $('.product-media img:not(.second)', card);
    const item = items[index];
    if (!image || !item?.src) return;

    delete card.dataset.pendingColorImage;
    card.dataset.pendingGalleryImage = item.src;
    card.dataset.pendingGalleryIndex = String(index);
    const preload = new Image();
    preload.decoding = 'async';
    preload.onload = () => {
      if (card.dataset.pendingGalleryImage !== item.src) return;
      if (item.srcset) image.setAttribute('srcset', item.srcset);
      else image.removeAttribute('srcset');
      image.src = item.src;
      image.alt = item.alt || image.alt;
      card.dataset.cardGalleryIndex = String(index);
      card.classList.add('has-gallery-selection');
      const matchingSwatch = $$('[data-card-color-swatch]', card).find((swatch) => (
        String(swatch.dataset.cardColorImageId || '') === String(item.id || '')
      ));
      if (matchingSwatch) {
        $$('[data-card-color-swatch]', card).forEach((swatch) => {
          const active = swatch === matchingSwatch;
          swatch.classList.toggle('is-active', active);
          swatch.setAttribute('aria-pressed', String(active));
        });
      }
      const status = $('[data-card-gallery-status]', card);
      if (status) status.textContent = `Imagem ${index + 1} de ${items.length}`;
      delete card.dataset.pendingGalleryImage;
      delete card.dataset.pendingGalleryIndex;
    };
    preload.onerror = () => {
      if (card.dataset.pendingGalleryImage === item.src) {
        delete card.dataset.pendingGalleryImage;
        delete card.dataset.pendingGalleryIndex;
      }
    };
    preload.sizes = image.sizes || '100vw';
    if (item.srcset) preload.srcset = item.srcset;
    preload.src = item.src;
  };
  const moveCardGallery = (control) => {
    const card = control?.closest('.product-card');
    const items = card ? cardGalleryItems(card) : [];
    if (!card || items.length < 2) return;
    const current = Number.parseInt(card.dataset.cardGalleryIndex, 10);
    const storedIndex = Number.isFinite(current) ? current : 0;
    const hoverImage = $('.product-media img.second', card);
    const hoverImageSrc = card.dataset.cardHoverImageSrc || '';
    const hoverIndex = items.findIndex((item) => String(item.src || '') === String(hoverImageSrc));
    const hoverImageVisible = (
      hoverIndex >= 0
      && card.matches(':hover')
      && !card.classList.contains('has-gallery-selection')
      && !card.classList.contains('has-color-selection')
      && hoverImage
      && getComputedStyle(hoverImage).display !== 'none'
      && Number.parseFloat(getComputedStyle(hoverImage).opacity || '0') > 0
    );
    const pendingIndex = Number.parseInt(card.dataset.pendingGalleryIndex, 10);
    const currentIndex = Number.isFinite(pendingIndex) ? pendingIndex : (hoverImageVisible ? hoverIndex : storedIndex);
    const direction = control.hasAttribute('data-card-gallery-prev') ? -1 : 1;
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    setCardGalleryImage(card, items, nextIndex);
  };
  const selectCardColor = (control) => {
    const card = control?.closest('.product-card');
    if (!card) return;

    $$('[data-card-color-swatch]', card).forEach((swatch) => {
      const active = swatch === control;
      swatch.classList.toggle('is-active', active);
      swatch.setAttribute('aria-pressed', String(active));
    });

    const image = $('.product-media img:not(.second)', card);
    const imageUrl = control.dataset.cardColorImage || '';
    if (!image || !imageUrl) return;

    delete card.dataset.pendingGalleryImage;
    delete card.dataset.pendingGalleryIndex;
    card.dataset.pendingColorImage = imageUrl;
    const preload = new Image();
    preload.decoding = 'async';
    preload.onload = () => {
      if (card.dataset.pendingColorImage !== imageUrl) return;
      const srcset = control.dataset.cardColorImageSrcset || '';
      if (srcset) image.setAttribute('srcset', srcset);
      else image.removeAttribute('srcset');
      image.src = imageUrl;
      image.alt = control.dataset.cardColorImageAlt || image.alt;
      card.classList.add('has-color-selection');
      const mediaId = control.dataset.cardColorImageId || '';
      const items = cardGalleryItems(card);
      const galleryIndex = items.findIndex((item) => String(item.id) === String(mediaId));
      if (galleryIndex >= 0) {
        card.dataset.cardGalleryIndex = String(galleryIndex);
        const status = $('[data-card-gallery-status]', card);
        if (status) status.textContent = `Imagem ${galleryIndex + 1} de ${items.length}`;
      }
      delete card.dataset.pendingColorImage;
    };
    preload.onerror = () => {
      if (card.dataset.pendingColorImage === imageUrl) delete card.dataset.pendingColorImage;
    };
    preload.sizes = image.sizes || '100vw';
    if (control.dataset.cardColorImageSrcset) preload.srcset = control.dataset.cardColorImageSrcset;
    preload.src = imageUrl;
  };

  /* ------------------------------------------------------------------ *
   * Quick add with size picker
   * ------------------------------------------------------------------ */
  const closeQuickAdd = (except) => $$('.product-card.quick-open').forEach((card) => {
    if (card !== except) {
      card.classList.remove('quick-open');
      $('[data-quick-add-open]', card)?.setAttribute('aria-expanded', 'false');
    }
  });
  const buildQuickAdd = (card) => {
    const panel = $('[data-quick-panel]', card);
    const variants = readJson($('[data-card-variants]', card));
    if (!panel || !variants) return false;
    if (panel.dataset.built === 'true') return true;
    panel.innerHTML = `<div class="quick-sizes">${variants.map((variant) => `
      <button type="button" data-quick-variant="${variant.id}"${variant.available ? '' : ' disabled'} aria-label="${escapeHtml(variant.title)}">${escapeHtml(variant.option || variant.title)}</button>`).join('')}
      </div>`;
    panel.dataset.built = 'true';
    return true;
  };

  /* ------------------------------------------------------------------ *
   * Sliders, marquees, timers
   * ------------------------------------------------------------------ */
  const timers = new Set();
  const setLoop = (fn, delay) => {
    const id = window.setInterval(fn, delay);
    timers.add(id);
    return id;
  };
  const clearLoopsIn = (root) => {
    if (root === document) return;
    $$('[data-timer-id]', root).forEach((node) => {
      const id = Number(node.dataset.timerId);
      window.clearInterval(id);
      timers.delete(id);
      delete node.dataset.timerId;
    });
    $$('[data-announcement], [data-rail]', root).forEach((node) => {
      node.urbController?.abort();
      delete node.urbController;
    });
    $$('[data-reveal]', root).forEach((node) => revealObserver?.unobserve(node));
  };
  /* Homepage slideshow is isolated in home-page.js. */
  const initAnnouncements = (root = document) => {
    $$('[data-announcement]', root).forEach((bar) => {
      if (bar.dataset.initialized) return;
      bar.dataset.initialized = 'true';
      const items = $$('[data-announcement-item]', bar);
      if (items.length < 2) {
        $('[data-announcement-prev]', bar)?.remove();
        $('[data-announcement-next]', bar)?.remove();
        return;
      }
      const controller = new AbortController();
      const { signal } = controller;
      bar.urbController = controller;
      let index = 0;
      let paused = false;
      const pauseOnHover = bar.dataset.pause !== 'false';
      const show = (next) => {
        index = (next + items.length) % items.length;
        items.forEach((item, i) => {
          item.classList.toggle('active', i === index);
          item.setAttribute('aria-hidden', String(i !== index));
        });
      };
      $('[data-announcement-prev]', bar)?.addEventListener('click', () => show(index - 1), { signal });
      $('[data-announcement-next]', bar)?.addEventListener('click', () => show(index + 1), { signal });
      if (pauseOnHover) {
        bar.addEventListener('mouseenter', () => { paused = true; }, { signal });
        bar.addEventListener('mouseleave', () => { paused = false; }, { signal });
        bar.addEventListener('focusin', () => { paused = true; }, { signal });
        bar.addEventListener('focusout', () => { paused = false; }, { signal });
      }
      if (!reduceMotion()) {
        const id = setLoop(() => { if (!document.hidden && !paused) show(index + 1); }, Number(bar.dataset.speed || 5000));
        bar.dataset.timerId = String(id);
      }
    });
  };
  /* Barra de anúncio dispensável: a escolha fica salva por seção. */
  const initAnnouncementDismiss = (root = document) => {
    $$('[data-announcement-dismissible]', root).forEach((bar) => {
      if (bar.dataset.dismissInitialized === 'true') return;
      bar.dataset.dismissInitialized = 'true';
      const key = `urbrand:announcement:${bar.dataset.announcementDismissible}`;
      let closed = null;
      try {
        closed = localStorage.getItem(key);
      } catch { /* private mode */ }
      if (closed === '1' && !window.Shopify?.designMode) {
        bar.hidden = true;
        return;
      }
      $('[data-announcement-close]', bar)?.addEventListener('click', () => {
        bar.hidden = true;
        try {
          localStorage.setItem(key, '1');
        } catch { /* private mode */ }
      });
    });
  };
  const initCountdowns = (root = document) => {
    $$('[data-countdown]', root).forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const end = Date.parse(section.dataset.countdown);
      if (Number.isNaN(end)) {
        section.hidden = true;
        return;
      }
      const finish = () => {
        const clock = $('.countdown-clock', section);
        if (clock) clock.innerHTML = `<strong>${escapeHtml(section.dataset.expiredText || 'ENCERRADO')}</strong>`;
        if (section.dataset.hideExpired === 'true') section.hidden = true;
      };
      const update = () => {
        const distance = end - Date.now();
        if (distance <= 0) {
          finish();
          const id = Number(section.dataset.timerId);
          window.clearInterval(id);
          timers.delete(id);
          return false;
        }
        const values = {
          days: Math.floor(distance / 86400000),
          hours: Math.floor((distance % 86400000) / 3600000),
          minutes: Math.floor((distance % 3600000) / 60000),
          seconds: Math.floor((distance % 60000) / 1000)
        };
        Object.entries(values).forEach(([key, value]) => {
          const node = $(`[data-${key}]`, section);
          if (node) node.textContent = String(value).padStart(2, '0');
        });
        return true;
      };
      if (update()) section.dataset.timerId = String(setLoop(update, 1000));
    });
  };
  /* Section-specific tabs are initialized by their dedicated controllers. */
  let revealObserver;
  const initReveal = (root = document) => {
    const elements = $$('[data-reveal]', root);
    if (document.body.dataset.revealEnabled !== 'true' || !('IntersectionObserver' in window) || reduceMotion()) {
      elements.forEach((element) => element.classList.add('visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          revealObserver.unobserve(entry.target);
        }
      }), { rootMargin: '0px 0px -5%' });
    }
    elements.forEach((element) => {
      if (!element.classList.contains('visible')) revealObserver.observe(element);
    });
  };
  /* Product sticky purchase behavior is isolated in product-page.js. */
  /* Horizontal card rails get keyboard + button controls. */
  const initRails = (root = document) => {
    const rails = $$('[data-rail]', root);
    if (root !== document && root.matches?.('[data-rail]')) rails.unshift(root);
    rails.forEach((rail) => {
      if (rail.dataset.initialized) {
        rail.urbRailSync?.();
        return;
      }
      rail.dataset.initialized = 'true';
      const track = $('[data-rail-track]', rail);
      if (!track) return;
      const controller = new AbortController();
      const { signal } = controller;
      rail.urbController = controller;
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        $('[data-rail-prev]', rail)?.toggleAttribute('disabled', track.scrollLeft <= 2);
        $('[data-rail-next]', rail)?.toggleAttribute('disabled', track.scrollLeft >= max);
        $('.rail-controls', rail)?.toggleAttribute('hidden', max <= 0);
      };
      rail.urbRailSync = sync;
      track.addEventListener('scroll', debounce(sync, 80), { passive: true, signal });
      window.addEventListener('resize', debounce(sync, 200), { signal });
      sync();
    });
  };

  /* ------------------------------------------------------------------ *
   * Global events
   * ------------------------------------------------------------------ */


  document.addEventListener('click', async (event) => {
    const target = event.target;
    const cardGalleryControl = target.closest('[data-card-gallery-prev], [data-card-gallery-next]');
    if (cardGalleryControl) {
      event.preventDefault();
      event.stopPropagation();
      moveCardGallery(cardGalleryControl);
      return;
    }
    const cardColorSwatch = target.closest('[data-card-color-swatch]');
    if (cardColorSwatch) {
      event.preventDefault();
      selectCardColor(cardColorSwatch);
    }
    const railControl = target.closest('[data-rail-prev], [data-rail-next]');
    if (railControl && !railControl.disabled) {
      const rail = railControl.closest('[data-rail]');
      const track = rail ? $('[data-rail-track]', rail) : null;
      if (track) {
        event.preventDefault();
        const direction = railControl.hasAttribute('data-rail-prev') ? -1 : 1;
        const distance = Math.max(240, track.clientWidth * .8) * direction;
        track.scrollBy({ left: distance, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    }
    if (target.closest('[data-wishlist-open]')) {
      event.preventDefault();
      syncWishlistUI();
      openDialog(wishlistDrawer);
    }
    if (target.closest('[data-wishlist-close]')) closeDialog(wishlistDrawer);

    const step = target.closest('[data-step]');
    if (step) {
      const wrap = step.closest('[data-quantity]');
      const input = wrap ? $('input', wrap) : null;
      if (input) {
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const increment = Math.max(1, Number(input.step || 1));
        const current = Number(input.value || min);
        const next = current + (step.dataset.step === 'up' ? increment : -increment);
        input.value = Math.min(max, Math.max(min, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    const wishlistToggle = target.closest('[data-wishlist-toggle]');
    if (wishlistToggle) {
      const items = getWishlist();
      const id = wishlistToggle.dataset.productId;
      const index = items.findIndex((item) => String(item.id) === String(id));
      if (index >= 0) {
        items.splice(index, 1);
        notify(t('wishlistRemoved', 'Removido dos favoritos'));
      } else {
        const limit = Math.max(10, Number(cfg.retention?.wishlistLimit || 40));
        if (items.length >= limit) {
          notify((t('wishlistLimit', 'Você atingiu o limite de favoritos.')).replace('__LIMIT__', String(limit)));
          return;
        }
        items.unshift({
          id,
          handle: wishlistToggle.dataset.productHandle || '',
          title: wishlistToggle.dataset.productTitle,
          url: wishlistToggle.dataset.productUrl,
          image: wishlistToggle.dataset.productImage,
          price: Number(wishlistToggle.dataset.productPrice),
          compareAtPrice: Number(wishlistToggle.dataset.productComparePrice || 0),
          variantId: wishlistToggle.dataset.productVariantId || '',
          variantTitle: wishlistToggle.dataset.productVariantTitle || '',
          available: wishlistToggle.dataset.productAvailable !== 'false',
          vendor: wishlistToggle.dataset.productVendor || '',
          productType: wishlistToggle.dataset.productType || ''
        });
        notify(t('wishlistAdded', 'Salvo nos favoritos'));
      }
      setWishlist(items);
    }
    const wishlistRemove = target.closest('[data-wishlist-remove]');
    if (wishlistRemove) setWishlist(getWishlist().filter((item) => String(item.id) !== String(wishlistRemove.dataset.wishlistRemove)));

    const filterDrawer = $('[data-filter-drawer]');
    const filterOpen = target.closest('[data-filter-open]');
    if (filterOpen) {
      filterOpen.setAttribute('aria-expanded', 'true');
      openPanel(filterDrawer, 'open');
    }
    if (target.closest('[data-filter-close]')) {
      closePanel(filterDrawer, 'open');
      $('[data-filter-open]')?.setAttribute('aria-expanded', 'false');
    }


    const zoom = target.closest('[data-media-zoom]');
    if (zoom) {
      const dialog = $('[data-media-lightbox]');
      const image = dialog ? $('img', dialog) : null;
      const responsiveGallery = dialog?.querySelector('[data-lightbox-images]');
      if (image && !responsiveGallery) {
        image.src = zoom.dataset.mediaZoom;
        image.alt = $('img', zoom)?.alt || '';
      }
      openDialog(dialog);
    }
    if (target.closest('[data-lightbox-close]')) closeDialog($('[data-media-lightbox]'));
    if (target.closest('[data-size-guide-open]')) openDialog($('[data-size-guide]'));
    if (target.closest('[data-size-guide-close]')) closeDialog($('[data-size-guide]'));

    const quickAddOpen = target.closest('[data-quick-add-open]');
    if (quickAddOpen) {
      event.preventDefault();
      const card = quickAddOpen.closest('.product-card');
      if (card) {
        closeQuickAdd(card);
        if (buildQuickAdd(card)) {
          const isOpen = card.classList.toggle('quick-open');
          quickAddOpen.setAttribute('aria-expanded', String(isOpen));
          if (isOpen) $('[data-quick-variant]:not([disabled])', card)?.focus();
        } else {
          window.location.href = card.dataset.productUrl || quickAddOpen.href || '#';
        }
      }
    }
    const quickVariant = target.closest('[data-quick-variant]');
    if (quickVariant) {
      event.preventDefault();
      const body = new FormData();
      body.append('id', quickVariant.dataset.quickVariant);
      body.append('quantity', '1');
      const cartApi = window.URBDrawers?.cart;
      if (cartApi?.add) {
        const done = await cartApi.add(body, quickVariant);
        if (done) closeQuickAdd();
      } else {
        const card = quickVariant.closest('.product-card');
        window.location.href = card?.dataset.productUrl || '#';
      }
    }
    if (!target.closest('.product-card')) closeQuickAdd();


    const share = target.closest('[data-share]');
    if (share) {
      const data = { title: share.dataset.shareTitle, url: share.dataset.shareUrl };
      if (navigator.share) navigator.share(data).catch(() => {});
      else {
        const copy = navigator.clipboard?.writeText
          ? navigator.clipboard.writeText(data.url)
          : Promise.reject(new Error('clipboard-unavailable'));
        copy.then(() => notify(t('linkCopied', 'Link copiado'))).catch(() => {
          const field = document.createElement('textarea');
          field.value = data.url;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          try { document.execCommand('copy'); notify(t('linkCopied', 'Link copiado')); } catch { /* optional feature */ }
          field.remove();
        });
      }
    }
    const whatsappBuy = target.closest('[data-whatsapp-buy]');
    if (whatsappBuy) {
      const root = whatsappBuy.closest('[data-product]');
      const form = whatsappBuy.closest('form');
      const variants = readJson($('[data-variants]', root));
      const variantId = $('[data-variant-id]', root)?.value;
      const variant = selectedVariant(root, variants, variantId);
      const number = String(whatsappBuy.dataset.whatsappNumber || '').replace(/\D/g, '');
      if (!variant?.available || !number || (form && !form.reportValidity())) {
        event.preventDefault();
        return;
      }
      syncWhatsAppBuyLink(root, variant);
    }
    if (target.closest('[data-back-to-top]')) {
      window.scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' });
      $('.skip')?.focus();
    }
  });

  document.addEventListener('change', async (event) => {
    const node = event.target;
    if (node.matches('[data-direct-variant-select]')) {
      updateProduct(node.closest('[data-product]'), node.value);
    }
    if (node.matches('[data-option], [data-option-select]')) {
      const selectedNode = node.matches('[data-option-select]') ? node.selectedOptions?.[0] : node;
      const productUrl = selectedNode?.dataset.productUrl;
      if (productUrl) {
        const targetUrl = new URL(productUrl, window.location.origin);
        const currentUrl = new URL(window.location.href);
        if (targetUrl.pathname !== currentUrl.pathname) {
          window.location.assign(targetUrl.href);
          return;
        }
      }
      const productRoot = node.closest('[data-product]');
      if (node.closest('.pdp__option--size')) {
        const variants = readJson($('[data-variants]', productRoot));
        selectNextAvailableColor(productRoot, variants);
      }
      updateProduct(productRoot);
    }
    if (node.matches('input[name="quantity"]')) {
      const root = node.closest('[data-product]');
      if (root) {
        const variants = readJson($('[data-variants]', root));
        const variantId = $('[data-variant-id]', root)?.value;
        syncWhatsAppBuyLink(root, selectedVariant(root, variants, variantId));
      }
    }
    if (node.matches('[data-facet-input]')) node.form?.submit();
    if (node.matches('[data-localization-select]')) node.form?.submit();
  });

  // Cancel AJAX product submits before Shopify's form-level analytics listener.
  // The successful cart/add response already emits product_added_to_cart.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    if (event.submitter && !event.submitter.matches('[data-add], [data-sticky-add]')) return;
    if (!form.reportValidity()) return;
    const cartApi = window.URBDrawers?.cart;
    if (!cartApi?.add) return;
    event.preventDefault();
    await cartApi.add(new FormData(form), event.submitter || $('[data-add]', form));
  }, { capture: true });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const facets = $('[data-filter-drawer].open');
      if (facets) {
        closePanel(facets, 'open');
        $('[data-filter-open]')?.setAttribute('aria-expanded', 'false');
      }
      closeQuickAdd();
    }
  });

  [wishlistDrawer, $('[data-media-lightbox]'), $('[data-size-guide]')]
    .filter(Boolean)
    .forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog(dialog);
      });
      dialog.addEventListener('close', () => window.URBDialog?.close ? (openOverlays.delete(dialog), syncLock()) : registerClose(dialog));
      dialog.addEventListener('keydown', (event) => trapFocus(event, dialog));
    });

  /* Back-to-top visibility. Header behavior lives in header.js. */
  const backToTop = $('[data-back-to-top]');
  const onGlobalScroll = () => backToTop?.classList.toggle('visible', window.scrollY > 700);
  window.addEventListener('scroll', onGlobalScroll, { passive: true });
  onGlobalScroll();


  const boot = (root = document) => {
    initAnnouncements(root);
    initAnnouncementDismiss(root);
    initCountdowns(root);
    initReveal(root);
    initRails(root);
    const products = $$('[data-product]', root);
    if (root !== document && root.matches?.('[data-product]')) products.unshift(root);
    products.forEach((productRoot) => {
      const variants = readJson($('[data-variants]', productRoot));
      const variantField = $('[data-variant-id]', productRoot);
      const variant = selectedVariant(productRoot, variants, variantField?.value);
      if (!variant) return;
      if (variantField) {
        variantField.value = String(variant.id);
        variantField.setAttribute('value', String(variant.id));
      }
      syncWhatsAppBuyLink(productRoot, variant);
    });
  };

  boot();
  syncWishlistUI();
  storeRecentlyViewed();
  renderRecentlyViewed();
  $$('[data-product]').forEach((root) => {
    if ($('[data-variants]', root)) markUnavailableOptions(root, readJson($('[data-variants]', root)) || []);
  });

  /* Public hook: call after injecting markup (recommendations, apps, custom sections). */
  cfg.refresh = (root = document) => {
    boot(root);
    syncWishlistUI();
    renderRecentlyViewed();
    $$('[data-product]', root).forEach((productRoot) => {
      const variantsNode = $('[data-variants]', productRoot);
      if (variantsNode) markUnavailableOptions(productRoot, readJson(variantsNode) || []);
    });
  };
  window.URB = cfg;

  document.addEventListener('shopify:section:load', (event) => cfg.refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    clearLoopsIn(event.target);
  });
})();
